import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import {
  TrophyIcon,
  SparklesIcon,
  FireIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

function XLogo({ className = 'w-6 h-6' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function formatDelta(value) {
  const n = Number(value) || 0;
  if (n > 0) return `+${n.toLocaleString()}`;
  return n.toLocaleString();
}

function formatPct(pct) {
  if (pct == null || Number.isNaN(pct)) return '—';
  return `${pct.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function shiftMonthKey(monthKey, delta) {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function medalStyles(rank) {
  if (rank === 1) {
    return {
      ring: 'ring-amber-400/60',
      bg: 'from-amber-400 via-yellow-300 to-amber-500',
      badge: '🥇',
      height: 'md:scale-105 md:-translate-y-2'
    };
  }
  if (rank === 2) {
    return {
      ring: 'ring-slate-300/60',
      bg: 'from-slate-300 via-slate-200 to-slate-400',
      badge: '🥈',
      height: ''
    };
  }
  return {
    ring: 'ring-orange-400/50',
    bg: 'from-orange-400 via-amber-600 to-orange-700',
    badge: '🥉',
    height: ''
  };
}

const VALeaderboard = () => {
  const { user } = useAuth();
  const isVa = user?.role === 'va';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [monthKey, setMonthKey] = useState(null);

  const fetchLeaderboard = useCallback(async (month) => {
    setLoading(true);
    try {
      const params = month ? { month } : {};
      const res = await api.get('/va/leaderboard', { params });
      if (res.data.success) {
        setData(res.data);
        setMonthKey((prev) => prev || res.data.period?.monthKey || '');
      }
    } catch (_) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard(monthKey || undefined);
  }, [fetchLeaderboard, monthKey]);

  const goMonth = (delta) => {
    const key = data?.period?.monthKey || monthKey;
    if (!key) return;
    setMonthKey(shiftMonthKey(key, delta));
  };

  const rankings = data?.rankings || [];
  const topThree = rankings.slice(0, 3);
  const dailyQuest = data?.dailyQuest;
  const todayQuest = dailyQuest?.today;
  const recentDays = dailyQuest?.recentDays || [];
  const myRank = rankings.find((r) => r.isMe)?.rank;
  const winner = data?.announcedWinner;

  if (loading && !data) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-indigo-500/20 bg-gradient-to-br from-slate-950 via-indigo-950 to-black text-white shadow-2xl shadow-indigo-900/30">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-sky-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-48 rounded-full bg-violet-600/15 blur-3xl" />
          <div className="absolute top-1/2 right-1/4 opacity-[0.07]">
            <XLogo className="w-48 h-48" />
          </div>
        </div>
        <div className="relative px-6 sm:px-10 py-10 sm:py-12">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-2xl space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-sky-200 border border-white/10">
                <TrophyIcon className="w-4 h-4 text-amber-300" />
                Monthly Bonus Arena
              </div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-3">
                <XLogo className="w-9 h-9 text-white shrink-0" />
                Leaderboard Bonus Program
              </h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Climb the ranks. Out-target the competition. Each month, the affiliate with the
                {' '}
                <span className="text-amber-300 font-semibold">most follow-backs</span>
                {' '}
                earns a
                {' '}
                <span className="text-emerald-300 font-bold">${data?.bonusAmount ?? 100} performance bonus</span>.
              </p>
              <p className="text-slate-400 text-sm leading-relaxed">
                Sharp targeting, steady output, and consistent follow volume are how you reach the top.
                Every follow sent is XP toward your monthly quest — every follow-back is a score on the board.
              </p>
            </div>
            <div className="flex flex-col items-end gap-3">
              <div className="flex items-center gap-2 rounded-xl bg-black/30 border border-white/10 px-3 py-2">
                <button type="button" onClick={() => goMonth(-1)} className="p-1 hover:text-sky-300" aria-label="Previous month">
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <span className="text-sm font-semibold min-w-[140px] text-center">{data?.period?.label}</span>
                <button type="button" onClick={() => goMonth(1)} className="p-1 hover:text-sky-300" aria-label="Next month">
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="text-right text-xs text-slate-400">
                Sorted by follow-backs · ${data?.bonusAmount ?? 100} prize
              </div>
            </div>
          </div>
        </div>
      </div>

      {winner && (
        <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-yellow-950/30 px-6 py-5 flex flex-wrap items-center gap-4">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 shadow-lg shadow-amber-500/30">
            <TrophyIcon className="w-8 h-8 text-white" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">Champion crowned</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">
              {winner.displayName}
              {' '}
              <span className="text-amber-600 dark:text-amber-400">won ${winner.bonusAmount ?? 100}</span>
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
              {formatDelta(winner.followersDelta)} follow-backs · {formatDelta(winner.followingDelta)} followed · {formatPct(winner.followBackPct)} follow-back rate
            </p>
          </div>
        </div>
      )}

      {isVa && todayQuest && (
        <div className="rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-800/80 p-6 shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center gap-1">
                <SparklesIcon className="w-4 h-4" />
                Your daily quest
              </p>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mt-1">Follow volume — {todayQuest.label}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Resets every day · only you can see this</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-black text-indigo-600 dark:text-indigo-400">{todayQuest.percent}%</p>
              {myRank != null && (
                <p className="text-xs text-gray-500">Monthly board rank #{myRank}</p>
              )}
            </div>
          </div>
          <div className="relative h-5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 transition-all duration-700 shadow-[0_0_20px_rgba(99,102,241,0.5)]"
              style={{ width: `${Math.min(100, todayQuest.percent)}%` }}
            />
            <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow">
              {todayQuest.followed.toLocaleString()} / {todayQuest.goal.toLocaleString()} followed
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center text-sm">
            <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 py-3 px-2">
              <p className="text-xs text-gray-500 uppercase">Followed</p>
              <p className="font-bold text-emerald-600 dark:text-emerald-400">{formatDelta(todayQuest.followed)}</p>
            </div>
            <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 py-3 px-2">
              <p className="text-xs text-gray-500 uppercase">Follow-backs</p>
              <p className="font-bold text-sky-600 dark:text-sky-400">{formatDelta(todayQuest.followersDelta)}</p>
            </div>
            <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 py-3 px-2">
              <p className="text-xs text-gray-500 uppercase">Follow-back %</p>
              <p className="font-bold text-violet-600 dark:text-violet-400">{formatPct(todayQuest.followBackPct)}</p>
            </div>
          </div>

          {recentDays.length > 0 && (
            <div className="mt-6 pt-5 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Last 3 days
              </p>
              <div className="space-y-3">
                {recentDays.map((day) => (
                  <div key={day.dayKey} className="rounded-xl bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{day.label}</span>
                      <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{day.percent}%</span>
                    </div>
                    <div className="relative h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden mb-2">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-indigo-400 to-violet-500"
                        style={{ width: `${Math.min(100, day.percent)}%` }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-400">
                      <span>
                        <span className="text-gray-400 uppercase">Followed </span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatDelta(day.followed)}</span>
                      </span>
                      <span>
                        <span className="text-gray-400 uppercase">Follow-backs </span>
                        <span className="font-semibold text-sky-600 dark:text-sky-400">{formatDelta(day.followersDelta)}</span>
                      </span>
                      <span>
                        <span className="text-gray-400 uppercase">Rate </span>
                        <span className="font-semibold text-violet-600 dark:text-violet-400">{formatPct(day.followBackPct)}</span>
                      </span>
                      <span className="text-gray-400">
                        {day.followed.toLocaleString()} / {day.goal.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Podium */}
      {topThree.length > 0 && (
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4 flex items-center gap-2">
            <FireIcon className="w-4 h-4 text-orange-500" />
            Top performers
          </h3>
          <div className="grid md:grid-cols-3 gap-4 items-end">
            {[topThree[1], topThree[0], topThree[2]].filter(Boolean).map((row) => {
              const style = medalStyles(row.rank);
              return (
                <div
                  key={`${row.rank}-${row.displayName}`}
                  className={`relative rounded-2xl p-5 bg-gradient-to-b ${style.bg} text-slate-900 shadow-xl ring-2 ${style.ring} ${style.height} ${row.rank === 1 ? 'order-2 md:order-none' : row.rank === 2 ? 'order-1' : 'order-3'}`}
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-2xl">{style.badge}</div>
                  <div className="text-center pt-2">
                    <p className="text-xs font-bold uppercase opacity-70">#{row.rank}</p>
                    <p className="text-lg font-black truncate mt-1 flex items-center justify-center gap-1">
                      {row.isMe && <StarIcon className="w-4 h-4 text-indigo-700 shrink-0" />}
                      {row.displayName}
                    </p>
                    <p className="text-3xl font-black mt-3">{formatDelta(row.followersDelta)}</p>
                    <p className="text-xs font-semibold uppercase opacity-80">Follow-backs</p>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-medium">
                      <div className="rounded-lg bg-black/10 px-2 py-1.5">
                        <div className="opacity-70">Followed</div>
                        <div className="font-bold">{formatDelta(row.followingDelta)}</div>
                      </div>
                      <div className="rounded-lg bg-black/10 px-2 py-1.5">
                        <div className="opacity-70">Rate</div>
                        <div className="font-bold">{formatPct(row.followBackPct)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full table */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <XLogo className="w-5 h-5 text-gray-900 dark:text-white" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Full leaderboard</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left py-3 px-4 w-14">#</th>
                <th className="text-left py-3 px-4">Affiliate</th>
                <th className="text-right py-3 px-4">Followed</th>
                <th className="text-right py-3 px-4">Follow-backs</th>
                <th className="text-right py-3 px-4">Follow-back %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rankings.map((row) => (
                <tr
                  key={`${row.rank}-${row.displayName}`}
                  className={row.isMe ? 'bg-indigo-50/80 dark:bg-indigo-950/30' : ''}
                >
                  <td className="py-3 px-4 font-bold text-gray-500">{row.rank}</td>
                  <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">
                    <span className="inline-flex items-center gap-1.5">
                      {row.rank <= 3 && <span>{medalStyles(row.rank).badge}</span>}
                      {row.displayName}
                      {row.isMe && (
                        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-indigo-600 text-white">You</span>
                      )}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-emerald-600 dark:text-emerald-400 font-medium">{formatDelta(row.followingDelta)}</td>
                  <td className="py-3 px-4 text-right text-sky-600 dark:text-sky-400 font-bold">{formatDelta(row.followersDelta)}</td>
                  <td className="py-3 px-4 text-right text-violet-600 dark:text-violet-400 font-medium">{formatPct(row.followBackPct)}</td>
                </tr>
              ))}
              {rankings.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-500">No rankings yet for this month.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VALeaderboard;
